import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Clock,
  XCircle,
  Code,
} from "lucide-react";
import { scansApi, groupsApi, ScanResult } from "@/api/client";
import EnvironmentSelect from "@/components/EnvironmentSelect";
import { formatDate, titleCase } from "@/lib/utils";

export default function Scans() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [envFilter, setEnvFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<string>(
    searchParams.get("group") || "",
  );
  const [expandedScan, setExpandedScan] = useState<string | null>(null);
  const [showApiExamples, setShowApiExamples] = useState(false);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Sync group filter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group") || "";
    if (urlGroup !== groupFilter) {
      setGroupFilter(urlGroup);
    }
  }, [searchParams]);

  const { data: scansData, isLoading } = useQuery({
    queryKey: ["scans", envFilter, groupFilter, limit, offset],
    queryFn: () =>
      scansApi.list({
        environment: envFilter || undefined,
        group: groupFilter || undefined,
        limit,
        offset,
      }),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const groups = groupsData?.groups || [];

  const { data: scanDetails } = useQuery({
    queryKey: ["scan", expandedScan],
    queryFn: () => (expandedScan ? scansApi.get(expandedScan) : null),
    enabled: !!expandedScan,
  });

  const toggleExpand = (scanId: string) => {
    setExpandedScan(expandedScan === scanId ? null : scanId);
  };

  const scans = scansData?.data || [];
  const total = scansData?.pagination?.total || 0;
  const results = scanDetails?.results || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={16} className="text-green-600" />;
      case "failed":
        return <XCircle size={16} className="text-red-600" />;
      case "in_progress":
        return <Clock size={16} className="text-blue-600 animate-pulse" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      in_progress: "bg-blue-100 text-blue-800",
      pending: "bg-gray-100 text-gray-800",
    };
    return styles[status] || styles.pending;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scan History</h1>
        <p className="text-gray-600">
          View server scan results and drift detection history
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <EnvironmentSelect
          value={envFilter}
          onChange={(value) => {
            setEnvFilter(value);
            setOffset(0);
          }}
        />

        <select
          value={groupFilter}
          onChange={(e) => {
            const value = e.target.value;
            setGroupFilter(value);
            setOffset(0);
            if (value) {
              setSearchParams((prev) => {
                prev.set("group", value);
                return prev;
              });
            } else {
              setSearchParams((prev) => {
                prev.delete("group");
                return prev;
              });
            }
          }}
          className="border rounded-lg px-3 py-2"
        >
          <option value="">All Groups</option>
          {groups.map((group) => (
            <option key={group.id} value={group.slug}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {/* Scans List */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : scans.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No scans found. Scans are automatically created when CI/CD pipelines
          submit scan results.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Scan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Environment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Servers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Components
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Drift
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scans.map((scan) => (
                  <React.Fragment key={scan.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpand(scan.id)}
                    >
                      <td className="pl-4">
                        {expandedScan === scan.id ? (
                          <ChevronDown size={16} className="text-gray-400" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <Search size={16} className="text-gray-400" />
                            <span className="font-medium text-gray-900">
                              {scan.scan_type === "initial"
                                ? "Initial Scan"
                                : scan.scan_type === "daily"
                                  ? "Daily Scan"
                                  : "Regular Scan"}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(scan.started_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-900">
                        {scan.environment_name}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {scan.servers_scanned}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {scan.components_checked}
                      </td>
                      <td className="px-6 py-4">
                        {scan.drift_count > 0 ? (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertTriangle size={16} />
                            {scan.drift_count}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle size={16} />0
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusBadge(scan.status)}`}
                        >
                          {getStatusIcon(scan.status)}
                          {scan.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {scan.source_url ? (
                          <a
                            href={scan.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            {scan.source || "View"}
                            <ExternalLink size={14} />
                          </a>
                        ) : (
                          <span className="text-gray-500">
                            {scan.source || scan.triggered_by}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expandedScan === scan.id && scanDetails && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-6 py-4">
                          <div className="text-sm">
                            <h4 className="font-medium mb-2">Scan Results</h4>
                            {results.length === 0 ? (
                              <p className="text-gray-500">No results</p>
                            ) : (
                              <table className="min-w-full">
                                <thead>
                                  <tr className="text-xs text-gray-500">
                                    <th className="text-left py-1">Server</th>
                                    <th className="text-left py-1">
                                      Component
                                    </th>
                                    <th className="text-left py-1">Service</th>
                                    <th className="text-left py-1">Expected</th>
                                    <th className="text-left py-1">Detected</th>
                                    <th className="text-left py-1">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {results.map((result: ScanResult) => (
                                    <tr
                                      key={result.id}
                                      className={
                                        result.is_drift ? "bg-red-50" : ""
                                      }
                                    >
                                      <td className="py-1">
                                        {result.server_hostname}
                                      </td>
                                      <td className="py-1">
                                        {result.component_name ||
                                          result.component_identifier}
                                      </td>
                                      <td className="py-1 text-gray-500">
                                        {result.service_name
                                          ? titleCase(result.service_name)
                                          : "-"}
                                      </td>
                                      <td className="py-1 font-mono text-xs">
                                        {result.expected_version || "-"}
                                      </td>
                                      <td className="py-1 font-mono text-xs">
                                        {result.detected_version}
                                      </td>
                                      <td className="py-1">
                                        {result.is_drift ? (
                                          <span className="text-red-600 flex items-center gap-1">
                                            <AlertTriangle size={14} />
                                            Drift
                                          </span>
                                        ) : (
                                          <span className="text-green-600 flex items-center gap-1">
                                            <CheckCircle size={14} />
                                            OK
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of{" "}
                {total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* API Examples */}
      <div className="border border-gray-200 rounded-lg bg-white">
        <button
          onClick={() => setShowApiExamples(!showApiExamples)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Code size={16} className="text-gray-500" />
            <span className="font-medium text-gray-700">API Examples</span>
          </div>
          {showApiExamples ? (
            <ChevronDown size={16} className="text-gray-400" />
          ) : (
            <ChevronRight size={16} className="text-gray-400" />
          )}
        </button>

        {showApiExamples && (
          <div className="px-4 pb-4 space-y-6 border-t border-gray-200">
            {/* Initial Scan */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">
                Initial Scan (Bootstrap) - Sets baseline versions
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {`curl -X POST http://localhost:8080/api/v1/scans/initial \\
  -H "X-API-Key: dpo_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "environment": "production",
    "scan_type": "initial",
    "source": "jenkins",
    "auto_create_services": true,
    "auto_create_components": true,
    "auto_create_servers": true,
    "servers": [
      {
        "hostname": "prod-api-01",
        "ip": "10.0.1.10",
        "service": "api-service",
        "components": [
          {
            "name": "API Server",
            "identifier": "api-server",
            "version": "2.5.1",
            "status": "running"
          }
        ]
      }
    ]
  }'`}
              </pre>
              <p className="mt-2 text-sm text-gray-500">
                Use{" "}
                <code className="bg-gray-100 px-1 rounded">
                  scan_type: "daily"
                </code>{" "}
                for daily snapshot scans.
              </p>
            </div>

            {/* Regular Scan */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                Regular Scan (Drift Detection) - Compares to expected versions
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {`curl -X POST http://localhost:8080/api/v1/scans/results \\
  -H "X-API-Key: dpo_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "environment": "production",
    "source": "monitoring-agent",
    "servers": [
      {
        "hostname": "prod-api-01",
        "components": [
          { "identifier": "api-server", "version": "2.5.2" }
        ]
      }
    ]
  }'`}
              </pre>
              <p className="mt-2 text-sm text-gray-500">
                This endpoint compares detected versions against expected
                versions and creates drift events for mismatches.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
